import React, { useState, useEffect } from 'react';
import { Users, Star, Shield, ShoppingCart, Video, MessageCircle, TrendingUp, Award, Calendar, Plus, AlertCircle, CheckCircle, CreditCard, Lock, BookOpen, Heart, Brain, BarChart3, Users as GroupIcon } from 'lucide-react';
import { GuruProfile, Session, GuruConnectApi } from '../../services/guruConnectApi';
import { SessionBooking } from './SessionBooking';
import { SessionsManager } from './SessionsManager';
import { MessagingInterface } from './MessagingInterface';
import { ContentMarketplace } from './ContentMarketplace';
import { ReviewSystem } from './ReviewSystem';
import { PaymentIntegration } from './PaymentIntegration';
import { AIGuruMatching } from './AIGuruMatching';
import { AdvancedAnalytics } from './AdvancedAnalytics';
import { GroupSessions } from './GroupSessions';

interface GuruConnectProps {
  userRole?: 'client' | 'guru' | 'admin';
}

export const GuruConnect: React.FC<GuruConnectProps> = ({ userRole = 'client' }) => {
  const [activeTab, setActiveTab] = useState<'discover' | 'my-gurus' | 'sessions' | 'marketplace' | 'messaging' | 'content' | 'reviews' | 'ai-matching' | 'analytics' | 'group-sessions'>('discover');
  const [selectedGuru, setSelectedGuru] = useState<GuruProfile | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterSpecialty, setFilterSpecialty] = useState('all');
  const [gurus, setGurus] = useState<GuruProfile[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showBooking, setShowBooking] = useState(false);
  const [bookingSuccess, setBookingSuccess] = useState<Session | null>(null);
  
  // New monetization features state
  const [showMessaging, setShowMessaging] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [showReviews, setShowReviews] = useState(false);
  const [currentUserId, setCurrentUserId] = useState('user123'); // In real app, get from auth
  const [paymentAmount, setPaymentAmount] = useState(0);
  const [paymentDescription, setPaymentDescription] = useState('');
  const [paymentSessionId, setPaymentSessionId] = useState<string | undefined>();
  const [paymentContentId, setPaymentContentId] = useState<string | undefined>();
  
  // Advanced features state
  const [userHealthProfile, setUserHealthProfile] = useState({
    conditions: ['diabetes', 'hypertension'],
    goals: ['weight_loss', 'better_nutrition', 'stress_management'],
    preferences: {
      session_type: 'video' as const,
      max_price: 150,
      availability: ['weekdays', 'evenings'],
      communication_style: 'motivational' as const,
      experience_level: 'intermediate' as const
    },
    health_data: {
      glucose_levels: [120, 135, 110, 125],
      weight_history: [180, 175, 172, 170],
      medication_compliance: 85,
      activity_level: 'moderate' as const,
      stress_level: 'moderate' as const
    }
  });

  // Load gurus on component mount
  useEffect(() => {
    loadGurus();
  }, []);

  const loadGurus = async () => {
    try {
      setLoading(true);
      setError('');
      const result = await GuruConnectApi.searchGurus({
        limit: 20,
        is_verified: true,
        search_term: searchQuery || undefined,
        specialties: filterSpecialty !== 'all' ? [filterSpecialty] : undefined,
      });
      setGurus(result.gurus);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load gurus');
      // Fallback to mock data for demo
      setGurus([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    loadGurus();
  };

  const handleBookingSuccess = (session: Session) => {
    setBookingSuccess(session);
    setShowBooking(false);
    // Refresh sessions tab
    setTimeout(() => {
      setBookingSuccess(null);
    }, 3000);
  };

  const renderGuruCard = (guru: GuruProfile) => (
    <div key={guru.id} className="bg-white dark:bg-gray-800 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 p-6">
      <div className="flex items-start space-x-4">
        <div className="flex-shrink-0">
          <img
            src={GuruConnectApi.getGuruAvatar(guru)}
            alt={GuruConnectApi.getGuruDisplayName(guru)}
            className="w-16 h-16 rounded-full"
          />
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center space-x-2 mb-2">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {GuruConnectApi.getGuruDisplayName(guru)}
            </h3>
            {guru.is_verified && (
              <Shield className="w-5 h-5 text-blue-500" />
            )}
          </div>
          
          <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
            {GuruConnectApi.getGuruRoleDisplay(guru.guru_role)}
          </p>
          
          <div className="flex items-center space-x-4 mb-3">
            <div className="flex items-center space-x-1">
              <Star className="w-4 h-4 text-yellow-400 fill-current" />
              <span className="text-sm font-medium">{guru.average_rating.toFixed(1)}</span>
              <span className="text-sm text-gray-500">({guru.total_reviews})</span>
            </div>
            <div className="flex items-center space-x-1">
              <TrendingUp className="w-4 h-4 text-green-500" />
              <span className="text-sm font-medium">{GuruConnectApi.formatTrustScore(guru.trust_score)}</span>
            </div>
            <span className="text-sm font-semibold text-green-600">
              {GuruConnectApi.formatPrice(guru.session_rate)}/hr
            </span>
          </div>
          
          <div className="flex flex-wrap gap-1 mb-3">
            {guru.certifications.slice(0, 2).map((cert, index) => (
              <span key={index} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                <Award className="w-3 h-3 mr-1" />
                {cert}
              </span>
            ))}
            {guru.certifications.length > 2 && (
              <span className="text-xs text-gray-500">+{guru.certifications.length - 2} more</span>
            )}
          </div>
          
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 line-clamp-2">{guru.bio}</p>
          
          <div className="flex space-x-2">
            <button 
              onClick={() => setSelectedGuru(guru)}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors duration-200"
            >
              View Profile
            </button>
            <button 
              onClick={() => {
                setSelectedGuru(guru);
                setShowBooking(true);
              }}
              className="flex-1 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors duration-200"
            >
              Book Session
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'discover':
        return (
          <div className="space-y-6">
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-lg p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Discover Health Professionals
              </h2>
              <p className="text-gray-600 dark:text-gray-300 mb-4">
                Connect with verified health coaches, nutritionists, and medical professionals. 
                All gurus are vetted and rated by our community.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                  <input
                    type="text"
                    placeholder="Search by name, specialty, or condition..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  />
                </div>
                <select
                  value={filterSpecialty}
                  onChange={(e) => setFilterSpecialty(e.target.value)}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                >
                  <option value="all">All Specialties</option>
                  <option value="diabetes">Diabetes</option>
                  <option value="weight_loss">Weight Loss</option>
                  <option value="mental_health">Mental Health</option>
                  <option value="fitness">Fitness</option>
                  <option value="nutrition">Nutrition</option>
                  <option value="cardiology">Cardiology</option>
                </select>
                <button
                  onClick={handleSearch}
                  disabled={loading}
                  className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium transition-colors disabled:opacity-50"
                >
                  {loading ? 'Searching...' : 'Search'}
                </button>
              </div>
            </div>
            
            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-4 mb-6">
                <div className="flex items-center space-x-2 text-red-600 dark:text-red-400">
                  <AlertCircle className="w-5 h-5" />
                  <span>{error}</span>
                </div>
              </div>
            )}
            
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                <span className="ml-3 text-gray-600 dark:text-gray-300">Loading gurus...</span>
              </div>
            ) : gurus.length === 0 ? (
              <div className="text-center py-12">
                <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                  No Gurus Found
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  Try adjusting your search criteria or check back later for new health professionals.
                </p>
              </div>
            ) : (
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {gurus.map(renderGuruCard)}
              </div>
            )}
          </div>
        );
        
      case 'my-gurus':
        return (
          <div className="text-center py-12">
            <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              No Gurus Yet
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Start by discovering and connecting with health professionals that match your needs.
            </p>
            <button 
              onClick={() => setActiveTab('discover')}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-md font-medium"
            >
              Discover Gurus
            </button>
          </div>
        );
        
      case 'sessions':
        return (
          <div className="space-y-6">
            {bookingSuccess && (
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md p-4">
                <div className="flex items-center space-x-2 text-green-600 dark:text-green-400">
                  <CheckCircle className="w-5 h-5" />
                  <span>Session booked successfully! Check your sessions below.</span>
                </div>
              </div>
            )}
            <SessionsManager userRole={userRole as 'guru' | 'client'} />
          </div>
        );
        
      case 'marketplace':
        return (
          <div className="space-y-6">
            <ContentMarketplace userRole={userRole} currentUserId={currentUserId} />
          </div>
        );
        
      case 'messaging':
        return (
          <div className="space-y-6">
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-lg p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Real-Time Messaging
              </h2>
              <p className="text-gray-600 dark:text-gray-300 mb-4">
                Connect with your health professionals through secure, real-time messaging.
              </p>
            </div>
            
            {selectedGuru ? (
              <MessagingInterface
                currentUserId={currentUserId}
                otherUserId={selectedGuru.id}
                otherUserName={GuruConnectApi.getGuruDisplayName(selectedGuru)}
                otherUserRole="guru"
              />
            ) : (
              <div className="text-center py-12">
                <MessageCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                  Select a Guru to Start Messaging
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  Choose a health professional from the Discover tab to begin a conversation.
                </p>
              </div>
            )}
          </div>
        );
        
      case 'content':
        return (
          <div className="space-y-6">
            <ContentMarketplace userRole={userRole} currentUserId={currentUserId} />
          </div>
        );
        
      case 'reviews':
        return (
          <div className="space-y-6">
            {selectedGuru ? (
              <ReviewSystem
                guruId={selectedGuru.id}
                currentUserId={currentUserId}
                userRole={userRole}
                onReviewSubmitted={() => {
                  // Refresh guru data after review
                  loadGurus();
                }}
              />
            ) : (
              <div className="text-center py-12">
                <Star className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                  Select a Guru to View Reviews
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  Choose a health professional from the Discover tab to see their reviews and ratings.
                </p>
              </div>
            )}
          </div>
        );
        
      case 'ai-matching':
        return (
          <div className="space-y-6">
            <AIGuruMatching
              userHealthProfile={userHealthProfile}
              onGuruSelect={(guru) => {
                setSelectedGuru(guru);
                setActiveTab('discover');
              }}
            />
          </div>
        );
        
      case 'group-sessions':
        return (
          <div className="space-y-6">
            <GroupSessions
              userRole={userRole}
              currentUserId={currentUserId}
            />
          </div>
        );
        
      case 'analytics':
        return (
          <div className="space-y-6">
            <AdvancedAnalytics
              userRole={userRole}
              userId={currentUserId}
              timeRange="30d"
            />
          </div>
        );
        
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Guru Connect
          </h1>
          <p className="text-gray-600 dark:text-gray-300">
            Connect with verified health professionals and discover personalized wellness solutions
          </p>
        </div>

        {/* Navigation Tabs */}
        <div className="border-b border-gray-200 dark:border-gray-700 mb-8">
          <nav className="-mb-px flex space-x-8 overflow-x-auto">
            {[
              { id: 'discover', label: 'Discover', icon: Users },
              { id: 'ai-matching', label: 'AI Matching', icon: Brain },
              { id: 'group-sessions', label: 'Group Sessions', icon: GroupIcon },
              { id: 'my-gurus', label: 'My Gurus', icon: Star },
              { id: 'sessions', label: 'Sessions', icon: Video },
              { id: 'marketplace', label: 'Content', icon: BookOpen },
              { id: 'messaging', label: 'Messages', icon: MessageCircle },
              { id: 'reviews', label: 'Reviews', icon: Heart },
              ...(userRole === 'guru' ? [{ id: 'analytics', label: 'Analytics', icon: BarChart3 }] : [])
            ].map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Main Content */}
        <div className="space-y-6">
          {renderTabContent()}
        </div>

        {/* Guru Profile Modal */}
        {selectedGuru && !showBooking && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-start mb-6">
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                    {GuruConnectApi.getGuruDisplayName(selectedGuru)}
                  </h2>
                  <button
                    onClick={() => setSelectedGuru(null)}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    ✕
                  </button>
                </div>
                
                <div className="space-y-6">
                  <div className="flex items-center space-x-4">
                    <img
                      src={GuruConnectApi.getGuruAvatar(selectedGuru)}
                      alt={GuruConnectApi.getGuruDisplayName(selectedGuru)}
                      className="w-20 h-20 rounded-full"
                    />
                    <div>
                      <p className="text-lg font-medium text-gray-900 dark:text-white">
                        {GuruConnectApi.getGuruRoleDisplay(selectedGuru.guru_role)}
                      </p>
                      <div className="flex items-center space-x-4 mt-1">
                        <div className="flex items-center space-x-1">
                          <Star className="w-4 h-4 text-yellow-400 fill-current" />
                          <span className="font-medium">{selectedGuru.average_rating.toFixed(1)}</span>
                          <span className="text-gray-500">({selectedGuru.total_reviews} reviews)</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <TrendingUp className="w-4 h-4 text-green-500" />
                          <span className="font-medium">{GuruConnectApi.formatTrustScore(selectedGuru.trust_score)} Trust Score</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2">About</h3>
                    <p className="text-gray-600 dark:text-gray-300">{selectedGuru.bio}</p>
                  </div>
                  
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Specialties</h3>
                    <div className="flex flex-wrap gap-2">
                      {selectedGuru.specialties.map((specialty, index) => (
                        <span key={index} className="px-3 py-1 bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded-full text-sm">
                          {specialty}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Certifications</h3>
                    <div className="flex flex-wrap gap-2">
                      {selectedGuru.certifications.map((cert, index) => (
                        <span key={index} className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                          <Award className="w-4 h-4 mr-1" />
                          {cert}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Experience</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <span className="text-sm text-gray-600 dark:text-gray-300">Years of Experience:</span>
                        <p className="font-medium">{selectedGuru.years_experience} years</p>
                      </div>
                      <div>
                        <span className="text-sm text-gray-600 dark:text-gray-300">Total Sessions:</span>
                        <p className="font-medium">{selectedGuru.total_sessions}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex space-x-3 pt-4">
                    <button 
                      onClick={() => setShowBooking(true)}
                      className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md font-medium transition-colors duration-200"
                    >
                      Book Session ({GuruConnectApi.formatPrice(selectedGuru.session_rate)}/hr)
                    </button>
                    <button 
                      onClick={() => {
                        setActiveTab('messaging');
                        setSelectedGuru(null);
                      }}
                      className="flex-1 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-900 dark:text-white px-4 py-2 rounded-md font-medium transition-colors duration-200"
                    >
                      Send Message
                    </button>
                  </div>
                  
                  <div className="flex space-x-3 pt-2">
                    <button 
                      onClick={() => {
                        setActiveTab('reviews');
                        setSelectedGuru(null);
                      }}
                      className="flex-1 bg-yellow-100 hover:bg-yellow-200 dark:bg-yellow-900 dark:hover:bg-yellow-800 text-yellow-800 dark:text-yellow-200 px-4 py-2 rounded-md font-medium transition-colors duration-200"
                    >
                      View Reviews
                    </button>
                    <button 
                      onClick={() => {
                        setPaymentAmount(selectedGuru.session_rate);
                        setPaymentDescription(`Session with ${GuruConnectApi.getGuruDisplayName(selectedGuru)}`);
                        setShowPayment(true);
                      }}
                      className="flex-1 bg-green-100 hover:bg-green-200 dark:bg-green-900 dark:hover:bg-green-800 text-green-800 dark:text-green-200 px-4 py-2 rounded-md font-medium transition-colors duration-200"
                    >
                      <CreditCard className="w-4 h-4 inline mr-1" />
                      Quick Pay
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Session Booking Modal */}
        {showBooking && selectedGuru && (
          <SessionBooking
            guru={selectedGuru}
            onClose={() => {
              setShowBooking(false);
              setSelectedGuru(null);
            }}
            onSuccess={handleBookingSuccess}
          />
        )}

        {/* Payment Integration Modal */}
        {showPayment && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <PaymentIntegration
              sessionId={paymentSessionId}
              contentId={paymentContentId}
              amount={paymentAmount}
              description={paymentDescription}
              onSuccess={(paymentId) => {
                setShowPayment(false);
                // Show success message
                alert(`Payment successful! Payment ID: ${paymentId}`);
              }}
              onError={(error) => {
                console.error('Payment error:', error);
              }}
              onCancel={() => setShowPayment(false)}
            />
          </div>
        )}
      </div>
    </div>
  );
}; 
